/**
 * How the form screen's Extend fields map onto the PDF's own widgets.
 *
 * The backend matches each Extend field's boxes to the template's widgets by
 * page and rectangle (`widget_names`, one per box) and, for checkboxes and
 * radios, records the value each widget exports when on (`on_values`). The
 * editor reports widget values by widget name; these helpers translate them
 * to Extend keys and back.
 */
import type { CasePrepUscisOverlayField } from "./types";
import { stringifyValue } from "./utils";

/** `overlay_fields` as the payload sends them once widgets are matched. */
export type FormCanvasField = CasePrepUscisOverlayField;

/** A field's box in PDF points, origin top-left. */
export type FieldBox = {
  pageIndex: number;
  left: number;
  top: number;
  width: number;
  height: number;
};

export type WidgetBindingKind = "text" | "choice" | "checkbox" | "radio";

export type WidgetBinding = {
  fieldName: string;
  kind: WidgetBindingKind;
  widgetName: string;
  /** Checkbox on-state, or the export value of this radio option. */
  onValue?: string;
  /** The enum option a radio box stands for. */
  option?: string;
};

export type WidgetBindings = {
  byWidget: Map<string, WidgetBinding[]>;
  byField: Map<string, WidgetBinding[]>;
  /** Fields with no usable widget; the form screen keeps an HTML input for them. */
  unmatched: Set<string>;
};

/** A form field as the loaded PDF has it: one entry per widget. */
export type PdfFormField = {
  kind: WidgetBindingKind | "other";
  /** Each widget's on-state for checkboxes and radios, else null. */
  onValues: Array<string | null>;
};

/** The PDF's "unchecked" state for checkboxes and radios. */
export const WIDGET_OFF = "Off";

type RawBox = Record<string, unknown>;

const isRawBox = (value: unknown): value is RawBox =>
  Boolean(value && typeof value === "object");

function rawBoxes(field: CasePrepUscisOverlayField): RawBox[] {
  const boxes = (field.bboxes ?? []).filter(isRawBox);
  if (boxes.length > 0) return boxes;
  return field.bbox ? [field.bbox as RawBox] : [];
}

function numberOr(value: unknown, fallback: number): number {
  const number = Number(value);
  return value != null && Number.isFinite(number) ? number : fallback;
}

function boxPageIndex(box: RawBox, field: CasePrepUscisOverlayField): number {
  const zeroBased = box.page_index ?? box.pageIndex ?? field.page_index;
  if (zeroBased != null) return Number(zeroBased);
  if (box.page == null) return 0;
  const pageNumber = Number(box.page);
  return pageNumber > 0 ? pageNumber - 1 : pageNumber;
}

/** Each box a field is drawn in; `bboxes` (one per radio option) win over `bbox`. */
export function fieldBoxes(field: CasePrepUscisOverlayField): FieldBox[] {
  return rawBoxes(field).map((box) => {
    const left = numberOr(box.left, 0);
    const top = numberOr(box.top, 0);
    const right = numberOr(box.right, left + 90);
    const bottom = numberOr(box.bottom, top + 18);
    return {
      pageIndex: boxPageIndex(box, field),
      left,
      top,
      width: Math.max(1, right - left),
      height: Math.max(1, bottom - top)
    };
  });
}

function fieldKind(field: FormCanvasField): WidgetBindingKind {
  const type = stringifyValue(field.field_type).trim().toLowerCase();
  if (type === "checkbox") return "checkbox";
  if (type === "radio") return "radio";
  if ((field.options ?? []).length > 0) {
    return (field.on_values ?? []).some(Boolean) ? "radio" : "choice";
  }
  return "text";
}

function radioOption(
  field: FormCanvasField,
  index: number,
  onValue: string
): string | undefined {
  const options = field.options ?? [];
  const boxes = field.widget_names ?? [];
  if (options.length === boxes.length) return options[index];
  const wanted = onValue.trim().toLowerCase();
  return options.find((option) => option.trim().toLowerCase() === wanted);
}

/**
 * One binding per matched box. A field is bound only when every box it has
 * matched a widget and the mapping round-trips (a toggle knows its on-state,
 * a radio box knows its option); anything less keeps the HTML fallback, so a
 * partial match can never clear or garble a value.
 */
export function buildWidgetBindings(fields: FormCanvasField[]): WidgetBindings {
  const byWidget = new Map<string, WidgetBinding[]>();
  const byField = new Map<string, WidgetBinding[]>();
  const unmatched = new Set<string>();
  for (const field of fields) {
    const names = field.widget_names ?? [];
    const kind = fieldKind(field);
    const bindings: WidgetBinding[] = [];
    let usable = names.length > 0 && names.length === rawBoxes(field).length;
    names.forEach((widgetName, index) => {
      if (!usable) return;
      if (!widgetName) {
        usable = false;
        return;
      }
      const onValue = field.on_values?.[index] ?? undefined;
      if (kind === "checkbox" || kind === "radio") {
        if (!onValue) {
          usable = false;
          return;
        }
        const option =
          kind === "radio" ? radioOption(field, index, onValue) : undefined;
        if (kind === "radio" && option === undefined) {
          usable = false;
          return;
        }
        bindings.push({
          fieldName: field.field_name,
          kind,
          widgetName,
          onValue,
          option
        });
        return;
      }
      bindings.push({ fieldName: field.field_name, kind, widgetName });
    });
    if (!usable || bindings.length === 0) {
      unmatched.add(field.field_name);
    } else {
      byField.set(field.field_name, bindings);
      for (const binding of bindings) {
        const list = byWidget.get(binding.widgetName) ?? [];
        list.push(binding);
        byWidget.set(binding.widgetName, list);
      }
    }
  }
  return { byWidget, byField, unmatched };
}

/**
 * Fields pointed at the widgets the loaded PDF has. A field keeps its template
 * widgets when the PDF has them all. Extend's filled copies rename each widget
 * to the field's key, so a field whose own name is a PDF field with one widget
 * per box binds to that, with the PDF's on-states. Anything else falls back.
 */
export function bindToPdfFields(
  fields: FormCanvasField[],
  pdfFields: ReadonlyMap<string, PdfFormField>
): FormCanvasField[] {
  return fields.map((field) => {
    const names = field.widget_names ?? [];
    if (names.length > 0 && names.every((name) => !name || pdfFields.has(name)))
      return field;
    const own = pdfFields.get(field.field_name);
    if (!own || own.onValues.length !== rawBoxes(field).length) {
      return { ...field, widget_names: null };
    }
    const toggle = own.kind === "checkbox" || own.kind === "radio";
    return {
      ...field,
      widget_names: own.onValues.map(() => field.field_name),
      on_values: toggle ? own.onValues : null
    };
  });
}

/** The loaded PDF's form fields by name; null when the PDF cannot be read. */
export async function readPdfFormFields(
  file: Blob
): Promise<Map<string, PdfFormField> | null> {
  try {
    const { PDFDocument, PDFCheckBox, PDFRadioGroup, PDFTextField } =
      await import("pdf-lib");
    const pdf = await PDFDocument.load(await file.arrayBuffer(), {
      ignoreEncryption: true,
      updateMetadata: false
    });
    const out = new Map<string, PdfFormField>();
    for (const field of pdf.getForm().getFields()) {
      const widgets = field.acroField.getWidgets();
      const toggle =
        field instanceof PDFCheckBox || field instanceof PDFRadioGroup;
      out.set(field.getName(), {
        kind:
          field instanceof PDFCheckBox
            ? "checkbox"
            : field instanceof PDFRadioGroup
            ? "radio"
            : field instanceof PDFTextField
            ? "text"
            : "other",
        onValues: widgets.map((widget) =>
          toggle ? widget.getOnValue()?.decodeText() ?? null : null
        )
      });
    }
    return out;
  } catch {
    return null;
  }
}

function isOn(value: string | undefined): boolean {
  return Boolean(value) && value !== WIDGET_OFF;
}

function booleanValue(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  const normalized = stringifyValue(value).trim().toLowerCase();
  return normalized === "true" || normalized === "yes" || normalized === "1";
}

/** The Extend value a field holds, read from its widgets' values. */
export function fieldValueFromWidgets(
  bindings: WidgetBinding[],
  widgetValues: Record<string, string>
): unknown {
  const [first] = bindings;
  if (!first) return undefined;
  if (first.kind === "checkbox") {
    return bindings.some(
      (binding) => widgetValues[binding.widgetName] === binding.onValue
    );
  }
  if (first.kind === "radio") {
    const selected = bindings.find(
      (binding) =>
        isOn(widgetValues[binding.widgetName]) &&
        widgetValues[binding.widgetName] === binding.onValue
    );
    return selected?.option ?? "";
  }
  return widgetValues[first.widgetName] ?? "";
}

/**
 * The Extend fields whose widgets changed between two editor snapshots, with
 * their new values. Only changed widgets count, so a field the PDF renders
 * differently from Extend's stored value (a comb SSN without its dashes) is
 * never reported as an edit.
 */
export function changedFieldValues(
  bindings: WidgetBindings,
  previous: Record<string, string>,
  next: Record<string, string>
): Record<string, unknown> {
  const fieldNames = new Set<string>();
  for (const [widgetName, widgetBindings] of bindings.byWidget) {
    if ((previous[widgetName] ?? "") !== (next[widgetName] ?? "")) {
      for (const binding of widgetBindings) fieldNames.add(binding.fieldName);
    }
  }
  const out: Record<string, unknown> = {};
  for (const fieldName of fieldNames) {
    out[fieldName] = fieldValueFromWidgets(
      bindings.byField.get(fieldName) ?? [],
      next
    );
  }
  return out;
}

/**
 * Widget values that put these Extend values into the PDF. Where several
 * fields share a widget (two checkboxes over one radio group), an "on" value
 * wins over "Off".
 */
export function widgetValuesForFields(
  bindings: WidgetBindings,
  values: Record<string, unknown>
): Record<string, string> {
  const out: Record<string, string> = {};
  const put = (widgetName: string, value: string) => {
    if (value === WIDGET_OFF && isOn(out[widgetName])) return;
    out[widgetName] = value;
  };
  const putField = (fieldBindings: WidgetBinding[], value: unknown) => {
    const [first] = fieldBindings;
    if (first.kind === "checkbox") {
      const on = booleanValue(value);
      for (const binding of fieldBindings) {
        put(binding.widgetName, on ? binding.onValue ?? "Yes" : WIDGET_OFF);
      }
      return;
    }
    if (first.kind === "radio") {
      const option = stringifyValue(value);
      const selected = fieldBindings.find(
        (binding) => binding.option === option
      );
      const groups = new Set(
        fieldBindings.map((binding) => binding.widgetName)
      );
      for (const widgetName of groups) {
        put(
          widgetName,
          selected && selected.widgetName === widgetName
            ? selected.onValue ?? WIDGET_OFF
            : WIDGET_OFF
        );
      }
      return;
    }
    for (const binding of fieldBindings) {
      put(binding.widgetName, stringifyValue(value));
    }
  };
  for (const [fieldName, value] of Object.entries(values)) {
    const fieldBindings = bindings.byField.get(fieldName);
    if (fieldBindings?.length) putField(fieldBindings, value);
  }
  return out;
}

/** The fields under a point on a page (PDF points), topmost-smallest first. */
export function fieldsAtPoint(
  fields: FormCanvasField[],
  pageIndex: number,
  x: number,
  y: number
): FormCanvasField[] {
  const hits: Array<{ field: FormCanvasField; area: number }> = [];
  for (const field of fields) {
    for (const box of fieldBoxes(field)) {
      if (
        box.pageIndex === pageIndex &&
        x >= box.left &&
        x <= box.left + box.width &&
        y >= box.top &&
        y <= box.top + box.height
      ) {
        hits.push({ field, area: box.width * box.height });
        break;
      }
    }
  }
  return hits.sort((a, b) => a.area - b.area).map((hit) => hit.field);
}
